/**
 * PublicHeader — V2 marketing header (v2/landing, v2/pricing, v2/business mockups)
 * ═══════════════════════════════════════════════════════════════════════════
 * Sticky white bar: logo · visible links · English/العربية · تسجيل الدخول (optional) · one CTA.
 * ≤980px (business: ≤900px) the links collapse into a menu button that opens a
 * side sheet (links, optional «في هذي الصفحة» shortcuts, CTA, login, language,
 * App Store badge, privacy/terms). Styles: src/styles/v2.css §6 (.sh-*, .ss-*).
 *
 * "One black pill per view": with ctaSolid="auto" (default) the CTA stays white
 * while any other `.btn.dark` of the page is on screen, and turns black once you
 * are past `solidAfter` px and no other black button is visible.
 *
 * Props (all optional)
 *   variant      'default' (landing / pricing / guides / legal / 404) | 'business' (/business:
 *                quieter English (+ login when given), permanent hairline, CTA «احجز ديمو» → #contact,
 *                phone bar = logo + menu only)
 *   active       key of the highlighted link. Default: the link whose `to` equals the current path.
 *   links        desktop links [{ key, label, to, opt }]. `to` may be a path ('/pricing'),
 *                a path + hash ('/#faq'), a same-page hash ('#faq') or an external URL.
 *                `opt: true` hides the link below 1180px. Default (AR / EN):
 *                كيف تشتغل /#how · للشركات /business · الأسعار /pricing ·
 *                مسح البطاقات /#scan (opt) · الأسئلة الشائعة /#faq (opt).
 *                Business default: الرئيسية / · الأسعار /pricing · للشركات /business.
 *   menuLinks    sheet links, same shape. Default: الرئيسية + the desktop links.
 *   sections     sheet shortcuts under «في هذي الصفحة» [{ key, label, to: '#id', icon }]
 *   sectionsTitle  default «في هذي الصفحة» / 'On this page'
 *   cta          { label, to } — default «ابدأ مجانًا» → /signup (business: «احجز ديمو» → #contact)
 *   login        { label, to } — default «تسجيل الدخول» → /login; false leaves the login link out of
 *                the bar and the sheet (/business: the owner sends companies their sign-in link directly)
 *   ctaSolid     'auto' | true | false
 *   solidAfter   scroll distance before the CTA may turn black in auto mode (default 320)
 *   bordered     always show the bottom hairline (business: true); otherwise it shows once scrolled
 *   className    extra classes on <header>
 *   label, adaptive, wrapClassName  accepted for backward compatibility; not used. The bar always
 *                sits in the shared `.sh-wrap` container (1280px, 1360px from 1800px; the landing and
 *                business mockups' .wrap), so it never shifts when you move between marketing pages,
 *                whatever content width a page uses below it.
 *
 * Geometry (measured against v2/landing and v2/business): bar 68px (phone ≤600px: 56px;
 * business 60px at 761–900px, 56px ≤760px), gutters 32 / 24 (≤980px; business ≤900px) / 16px (≤600px;
 * business ≤760px). Tablet 601–980px (default variant): links move to the sheet, English + login + CTA
 * stay in the bar, and the sheet leaves out those three (links, App Store badge, privacy/terms).
 *
 * In-page links (#id or /path#id on the current path) scroll smoothly so the section lands
 * just under the bar (src/components/v2/scrollToHash.js); links to another page carry the
 * hash and App's ScrollToTop scrolls once the page has rendered.
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft01Icon, ArrowRight01Icon, Cancel01Icon, Globe02Icon, Menu01Icon } from '@hugeicons/core-free-icons';
import { toggleLocale, useLocale } from '../lib/i18n';
import { APP_STORE_URL } from '../lib/appStore.js';
import BrandLogo from './BrandLogo.jsx';
import Icon from './v2/Icon.jsx';
import { scrollToHash } from './v2/scrollToHash.js';
import { lockScroll } from './v2/scrollLock.js';

const isExternal = (url) => /^(https?:|mailto:|tel:)/i.test(url || '');

function splitTo(to = '') {
  const i = to.indexOf('#');
  if (i === -1) return { path: to, hash: '' };
  return { path: to.slice(0, i), hash: to.slice(i) };
}

/**
 * SmartLink — router link that understands '#id' and '/path#id'.
 * Same-page hashes update the URL and scroll under the sticky header.
 */
export function SmartLink({ to = '/', className, onNavigate, children, ...rest }) {
  const location = useLocation();
  const navigate = useNavigate();

  if (isExternal(to)) {
    return (
      <a href={to} className={className} target="_blank" rel="noopener noreferrer" onClick={onNavigate} {...rest}>
        {children}
      </a>
    );
  }

  const { path, hash } = splitTo(to);
  const samePage = !path || path === location.pathname;

  if (hash && samePage) {
    return (
      <a
        href={`${location.pathname}${location.search}${hash}`}
        className={className}
        onClick={(e) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
          e.preventDefault();
          onNavigate?.(e);
          if (location.hash === hash) scrollToHash(hash);
          else navigate({ pathname: location.pathname, search: location.search, hash });
        }}
        {...rest}
      >
        {children}
      </a>
    );
  }

  return (
    <Link to={to} className={className} onClick={onNavigate} {...rest}>
      {children}
    </Link>
  );
}

function useHeaderStrings(ar) {
  const t = (a, e) => (ar ? a : e);
  return {
    t,
    nav: t('تنقل الموقع', 'Site navigation'),
    menu: t('فتح القائمة', 'Open menu'),
    closeMenu: t('إغلاق القائمة', 'Close menu'),
    sheet: t('قائمة الموقع', 'Site menu'),
    home: t('الرئيسية', 'Home'),
    how: t('كيف تشتغل', 'How it works'),
    business: t('للشركات', 'For business'),
    pricing: t('الأسعار', 'Pricing'),
    scan: t('مسح البطاقات', 'Card scanning'),
    faq: t('الأسئلة الشائعة', 'FAQ'),
    start: t('ابدأ مجانًا', 'Start free'),
    demo: t('احجز ديمو', 'Book a demo'),
    login: t('تسجيل الدخول', 'Log in'),
    privacy: t('سياسة الخصوصية', 'Privacy'),
    terms: t('شروط الاستخدام', 'Terms'),
    onPage: t('في هذي الصفحة', 'On this page'),
    appStore: t('حمّل CardPass من App Store', 'Download CardPass on the App Store')
  };
}

export default function PublicHeader({
  variant = 'default',
  active,
  links,
  menuLinks,
  sections,
  sectionsTitle,
  cta,
  login,
  ctaSolid = 'auto',
  solidAfter = 320,
  bordered,
  className = '',
  // eslint-disable-next-line no-unused-vars
  label, adaptive, wrapClassName
}) {
  const ar = useLocale() === 'ar';
  const S = useHeaderStrings(ar);
  const location = useLocation();
  const biz = variant === 'business';
  const headerRef = useRef(null);
  const menuBtnRef = useRef(null);
  const closeBtnRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [autoSolid, setAutoSolid] = useState(false);
  // The menu sheet portals into <body>; mount it after hydration so the
  // prerendered markup (scripts/postbuild-seo.mjs) matches the first render.
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  const deskLinks = links || (biz
    ? [
        { key: 'home', label: S.home, to: '/' },
        { key: 'pricing', label: S.pricing, to: '/pricing' },
        { key: 'business', label: S.business, to: '/business' }
      ]
    : [
        { key: 'how', label: S.how, to: '/#how' },
        { key: 'business', label: S.business, to: '/business' },
        { key: 'pricing', label: S.pricing, to: '/pricing' },
        { key: 'scan', label: S.scan, to: '/#scan', opt: true },
        { key: 'faq', label: S.faq, to: '/#faq', opt: true }
      ]);
  const sheetLinks = menuLinks || (deskLinks.some((l) => l.to === '/')
    ? deskLinks
    : [{ key: 'home', label: S.home, to: '/' }, ...deskLinks]);
  const ctaItem = { label: biz ? S.demo : S.start, to: biz ? '#contact' : '/signup', ...(cta || {}) };
  const loginItem = login === false ? null : { label: S.login, to: '/login', ...(login || {}) };
  const isBordered = bordered ?? biz;

  const activeKey = active ?? deskLinks.find((l) => !splitTo(l.to).hash && splitTo(l.to).path === location.pathname)?.key;
  const isOn = (l) => (active != null ? l.key === active : (!splitTo(l.to).hash && splitTo(l.to).path === location.pathname));

  // Close the sheet on navigation.
  useEffect(() => { setOpen(false); }, [location.pathname, location.hash]);

  // Sheet: Escape, scroll lock, focus in/out, close when the bar shows its links again.
  useEffect(() => {
    if (!open) return undefined;
    const unlock = lockScroll();
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const mq = window.matchMedia ? window.matchMedia(biz ? '(max-width: 900px)' : '(max-width: 980px)') : null;
    const onMq = () => { if (mq && !mq.matches) setOpen(false); };
    window.addEventListener('keydown', onKey);
    mq?.addEventListener?.('change', onMq);
    const id = window.setTimeout(() => closeBtnRef.current?.focus({ preventScroll: true }), 40);
    const btn = menuBtnRef.current;
    return () => {
      unlock();
      window.clearTimeout(id);
      window.removeEventListener('keydown', onKey);
      mq?.removeEventListener?.('change', onMq);
      if (document.activeElement && document.activeElement.closest?.('.ss')) btn?.focus({ preventScroll: true });
    };
  }, [open, biz]);

  // Hairline once scrolled + "one black pill per view" for the CTA.
  useEffect(() => {
    let raf = 0;
    const check = () => {
      raf = 0;
      const y = window.scrollY;
      setScrolled(y > 4);
      if (ctaSolid !== 'auto') return;
      const hh = headerRef.current ? headerRef.current.getBoundingClientRect().height : 0;
      const vh = window.innerHeight;
      let other = false;
      const darks = document.querySelectorAll('.btn.dark');
      for (let i = 0; i < darks.length && !other; i += 1) {
        const b = darks[i];
        if (b.closest('.sh, .ss')) continue;
        const r = b.getBoundingClientRect();
        if (!r.width || !r.height || r.bottom <= hh || r.top >= vh) continue;
        const cs = window.getComputedStyle(b);
        if (cs.visibility === 'hidden' || Number(cs.opacity) === 0) continue;
        other = true;
      }
      setAutoSolid(y > solidAfter && !other);
    };
    const schedule = () => { if (!raf) raf = window.requestAnimationFrame(check); };
    check();
    const t1 = window.setTimeout(check, 400);
    const t2 = window.setTimeout(check, 1500);
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [ctaSolid, solidAfter, location.pathname]);

  const solid = ctaSolid === 'auto' ? autoSolid : !!ctaSolid;
  const close = () => setOpen(false);
  const chevron = ar ? ArrowLeft01Icon : ArrowRight01Icon;
  const langLabel = ar ? 'English' : 'العربية';
  const langCode = ar ? 'en' : 'ar';

  const sheet = (
    <>
      <div className={`ss-scrim${open ? ' open' : ''}`} onClick={close} aria-hidden="true" />
      <aside
        id="site-menu"
        className={`ss${open ? ' open' : ''}${biz ? ' biz' : ''}`}
        aria-label={S.sheet}
        aria-hidden={open ? undefined : true}
        dir={ar ? 'rtl' : 'ltr'}
      >
        <div className="ss-head">
          <Link to="/" className="sh-logo" aria-label="CardPass" onClick={close}><BrandLogo height={26} /></Link>
          <button type="button" className="ss-close" ref={closeBtnRef} onClick={close} aria-label={S.closeMenu}>
            <Icon icon={Cancel01Icon} />
          </button>
        </div>
        <nav className="ss-links" aria-label={S.nav}>
          {sheetLinks.map((l) => {
            const on = isOn(l);
            return (
              <SmartLink key={l.key || l.to} to={l.to} className={on ? 'on' : undefined} onNavigate={close}
                aria-current={on ? 'page' : undefined}>
                <span>{l.label}</span>
                {on ? null : <Icon icon={chevron} />}
              </SmartLink>
            );
          })}
        </nav>
        {sections && sections.length ? (
          <>
            <div className="ss-grp">{sectionsTitle || S.onPage}</div>
            <div className="ss-quick">
              {sections.map((s) => (
                <SmartLink key={s.key || s.to} to={s.to} onNavigate={close}>
                  {s.icon ? <Icon icon={s.icon} /> : null}
                  <span>{s.label}</span>
                </SmartLink>
              ))}
            </div>
          </>
        ) : null}
        <div className="ss-sp" />
        <div className="ss-foot">
          {biz ? (
            <>
              {loginItem ? <SmartLink to={loginItem.to} className="btn" onNavigate={close}>{loginItem.label}</SmartLink> : null}
              <SmartLink to={ctaItem.to} className="btn dark" onNavigate={close}>{ctaItem.label}</SmartLink>
            </>
          ) : (
            <>
              <SmartLink to={ctaItem.to} className="btn dark" onNavigate={close}>{ctaItem.label}</SmartLink>
              {loginItem ? <SmartLink to={loginItem.to} className="btn" onNavigate={close}>{loginItem.label}</SmartLink> : null}
            </>
          )}
          <div className="ss-row">
            <button type="button" className={`sh-lang${biz ? ' ltr' : ''}`} onClick={toggleLocale} lang={langCode}>
              {biz ? null : <Icon icon={Globe02Icon} />}{langLabel}
            </button>
            <a className="asb" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" aria-label={S.appStore}>
              <img src="/app-store-badge.svg" alt="Download on the App Store" />
            </a>
          </div>
          <div className="ss-legal">
            <Link to="/privacy" onClick={close}>{S.privacy}</Link>
            <Link to="/terms" onClick={close}>{S.terms}</Link>
          </div>
        </div>
      </aside>
    </>
  );

  return (
    <>
      <header
        ref={headerRef}
        className={`sh${biz ? ' biz' : ''}${scrolled ? ' sc' : ''}${isBordered ? ' bordered' : ''}${solid ? ' past' : ''}${className ? ` ${className}` : ''}`}
      >
        <div className="sh-wrap sh-bar">
          <Link to="/" className="sh-logo" aria-label="CardPass">
            <BrandLogo height={biz ? 28 : 30} />
          </Link>
          <nav className="sh-links" aria-label={S.nav}>
            {deskLinks.map((l) => {
              const on = l.key != null && l.key === activeKey;
              return (
                <SmartLink key={l.key || l.to} to={l.to} className={`${on ? 'on' : ''}${l.opt ? ' opt' : ''}`.trim() || undefined}
                  aria-current={on ? 'page' : undefined}>
                  {l.label}
                </SmartLink>
              );
            })}
          </nav>
          <div className="sh-end">
            {biz ? (
              <>
                <button type="button" className="sh-lang ltr" onClick={toggleLocale} lang={langCode}>{langLabel}</button>
                {loginItem ? <SmartLink to={loginItem.to} className="sh-login">{loginItem.label}</SmartLink> : null}
              </>
            ) : (
              <>
                <button type="button" className="sh-lang" onClick={toggleLocale} lang={langCode}>
                  <Icon icon={Globe02Icon} />{langLabel}
                </button>
                {loginItem ? <SmartLink to={loginItem.to} className="btn sh-ghost">{loginItem.label}</SmartLink> : null}
              </>
            )}
            <SmartLink to={ctaItem.to} className={`btn sh-cta${solid ? ' dark' : ''}`}>{ctaItem.label}</SmartLink>
            <button
              type="button"
              className="sh-menu"
              ref={menuBtnRef}
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-controls="site-menu"
              aria-label={open ? S.closeMenu : S.menu}
            >
              <Icon icon={Menu01Icon} />
            </button>
          </div>
        </div>
      </header>
      {mounted ? createPortal(sheet, document.body) : null}
    </>
  );
}
